<template>
  <BaseLayout title="Ask AI">
    <div class="container-fluid d-flex flex-column h-100">
      <TaskSelector
        :groupedOptions="groupedOptions"
        :selectedSubtaskId="selectedSubtaskId"
        @update:selectedSubtaskId="onSelectTask"
      />
      <div v-if="loading" class="text-center my-4">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Loading…</span>
        </div>
      </div>
      <AskAIChat
        v-else
        :key="chatKey"
        :subtaskId="selectedSubtaskId"
        :conversationId="selectedConversationId"
        :messages="selectedMessages"
        @update:conversationId="onConversationId"
        @conversation-created="onConversationCreated"
      />
    </div>

    <template v-slot:right-sidebar>
      <ChatList
        :conversations="filteredConversations"
        :selectedId="selectedConversationId"
        @select="selectConversation"
        @new-chat="newChat"
      />
    </template>

    <StemErrorModal ref="stemErrorModal" />
  </BaseLayout>
</template>

<script>
import BaseLayout from '@/components/BaseLayout.vue';
import StemErrorModal from '@/components/StemErrorModal.vue';
import TaskSelector from '@/components/TaskSelector.vue';
import ChatList from '@/components/ChatList.vue';
import AskAIChat from '@/components/AskAIChat.vue';
import ConversationController from '@/controllers/ConversationController';
import HomeworkController from '@/controllers/HomeworkController';
import SubtaskController from '@/controllers/SubtaskController';

export default {
  name: 'AskAIView',
  components: { BaseLayout, StemErrorModal, TaskSelector, ChatList, AskAIChat },
  data() {
    return {
      loading: true,
      conversations: [],
      groupedOptions: [],
      selectedSubtaskId: null,
      selectedConversationId: null,
      selectedMessages: [],
      // Bumped to force AskAIChat to remount (reset its history) on a conversation switch.
      chatKey: 0,
    };
  },
  computed: {
    filteredConversations() {
      if (this.selectedSubtaskId == null || this.selectedSubtaskId === '') {
        return this.conversations;
      }
      return this.conversations.filter(
        (c) => c.subtask_id != null && String(c.subtask_id) === String(this.selectedSubtaskId)
      );
    },
  },
  async created() {
    const querySubtask = this.$route.query.subtask;
    if (querySubtask != null && querySubtask !== '') {
      this.selectedSubtaskId = String(querySubtask);
    }
    await Promise.all([this.loadConversations(), this.loadTasks()]);
    this.loading = false;
  },
  methods: {
    async loadConversations() {
      try {
        this.conversations = await ConversationController.getCurrentUserConversations();
      } catch (error) {
        this.conversations = [];
        if (this.$refs.stemErrorModal) this.$refs.stemErrorModal.show('Error', error.message);
      }
    },
    async loadTasks() {
      try {
        const homeworks = await HomeworkController.fetchHomeworksForStudent();
        const groups = [];
        for (const hw of homeworks || []) {
          if (!hw.id) continue;
          let subtasks = [];
          try {
            subtasks = await SubtaskController.fetchSubtasksByHomeworkId(hw.id);
          } catch (e) {
            subtasks = [];
          }
          groups.push({
            title: hw.title || 'Homework',
            items: (subtasks || []).map((s) => ({ title: s.title || 'Task', value: String(s.id) })),
          });
        }
        this.groupedOptions = groups;
      } catch (error) {
        this.groupedOptions = [];
      }
    },
    onSelectTask(subtaskId) {
      this.selectedSubtaskId = subtaskId;
      this.newChat();
    },
    selectConversation(conv) {
      this.selectedConversationId = String(conv.id);
      this.selectedMessages = (conv.messages || []).map((m) => ({ ...m }));
      this.chatKey += 1;
    },
    newChat() {
      this.selectedConversationId = null;
      this.selectedMessages = [];
      this.chatKey += 1;
    },
    onConversationId(id) {
      this.selectedConversationId = id ? String(id) : null;
    },
    async onConversationCreated(id) {
      this.selectedConversationId = id ? String(id) : null;
      // Refresh the sidebar so the freshly-created conversation appears (kept selected;
      // chatKey is not bumped so the just-streamed messages stay on screen).
      await this.loadConversations();
    },
  },
};
</script>
